import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Fab,
  Autocomplete,
  Avatar,
  Chip,
  Grid,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Egg as EggIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import {
  useEggProductions,
  useEggProductionStats,
  useCreateEggProduction,
} from '../hooks/useEggProduction';
import { useBirdProcurements, useBirds, useBirdBlocks } from '../hooks/useBirds';
import { useNavigate } from 'react-router-dom';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
  rgb: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, sub, rgb }) => (
  <Paper
    sx={{
      p: 2.5,
      background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
      border: `1px solid rgba(${rgb},0.15)`,
      borderRadius: 2,
      transition: 'all 0.2s ease',
      '&:hover': {
        border: `1px solid rgba(${rgb},0.35)`,
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      },
    }}
  >
    <Box display="flex" alignItems="flex-start" gap={2}>
      <Avatar
        sx={{
          bgcolor: `rgba(${rgb},0.12)`,
          color: `rgb(${rgb})`,
          width: 44,
          height: 44,
          flexShrink: 0,
        }}
      >
        {icon}
      </Avatar>
      <Box flex={1}>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{
            letterSpacing: '0.8px',
            color: 'rgba(255,152,0,0.7)',
            fontSize: '0.65rem',
            display: 'block',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: `rgb(${rgb})`, lineHeight: 1.2, mt: 0.25 }}
        >
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}>
          {sub}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const EggProductionPage: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    birds_id: '',
    block_id: '',
    production_date: new Date().toISOString().split('T')[0],
    crate_quantity: '',
    surplus_quantity: '',
    broken_quantity: '',
    remarks: '',
  });

  const [filters, setFilters] = useState({
    procurement_id: '',
    date_from: '',
    date_to: '',
  });

  const navigate = useNavigate();

  const {
    data: productionsData,
    isLoading,
    error,
    refetch,
    isFetching: productionsFetching,
  } = useEggProductions(filters);
  const { data: stats, isFetching: statsFetching } = useEggProductionStats();
  const { data: birdsData, isFetching: birdsFetching } = useBirds();
  const { data: procurementsData, isFetching: procurementsFetching } = useBirdProcurements();

  const createMutation = useCreateEggProduction();

  const selectedBirdIdForBlocks = formData.birds_id ? parseInt(formData.birds_id) : 0;
  const { data: selectedBirdBlocks = [] } = useBirdBlocks(selectedBirdIdForBlocks);

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Egg Production Data
            </Typography>
            <Typography variant="body2" paragraph>
              {error instanceof Error
                ? error.message
                : 'An error occurred while fetching egg production data. Please try again.'}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()} sx={{ color: '#fff' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const handleOpenDialog = () => {
    setFormData({
      birds_id: '',
      block_id: '',
      production_date: new Date().toISOString().split('T')[0],
      crate_quantity: '',
      surplus_quantity: '',
      broken_quantity: '',
      remarks: '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleClearFilters = () => {
    setFilters({ procurement_id: '', date_from: '', date_to: '' });
  };

  const getFilteredTotals = () => {
    const productions = productionsData?.data || [];
    const totalEggs = productions.reduce((sum, p) => sum + (p.summary.total_eggs || 0), 0);
    const totalCrates = Math.floor(totalEggs / 30);
    const totalPieces = totalEggs % 30;
    const totalBroken = productions.reduce((sum, p) => sum + (p.summary.total_broken || 0), 0);
    const goodEggs = totalEggs - totalBroken;
    const goodCrates = Math.floor(goodEggs / 30);
    const goodPieces = goodEggs % 30;
    return { totalEggs, totalCrates, totalPieces, totalBroken, goodEggs, goodCrates, goodPieces };
  };

  const filteredTotals = getFilteredTotals();

  const handleSubmit = async () => {
    try {
      const data: any = {
        birds_id: parseInt(formData.birds_id),
        production_date: formData.production_date,
        crate_quantity: parseInt(formData.crate_quantity),
        surplus_quantity: parseInt(formData.surplus_quantity),
        broken_quantity: parseInt(formData.broken_quantity || '0'),
        remarks: formData.remarks,
      };
      if (formData.block_id) data.block_id = parseInt(formData.block_id);
      await createMutation.mutateAsync(data as any);
      handleCloseDialog();
      refetch();
    } catch (err) {
      console.error('Error saving egg production:', err);
    }
  };

  const handleViewDetails = (birdId: number) => {
    navigate(`/eggs/production/bird?birdId=${birdId}`);
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return <ArrowUpwardIcon fontSize="small" sx={{ color: '#4caf50' }} />;
      case 'declining':
        return <ArrowDownwardIcon fontSize="small" sx={{ color: '#f44336' }} />;
      default:
        return <RemoveIcon fontSize="small" sx={{ color: '#ff9800' }} />;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'bird_info',
      headerName: 'Bird Batch',
      flex: 1.8,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams) => {
        const birdInfo = params.row.overview.bird_info;
        return (
          <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
            <Typography variant="body2" fontWeight={600}>
              {birdInfo.color} - {birdInfo.procurement}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {birdInfo.current_quantity} birds • {birdInfo.age_in_weeks} weeks
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'total_eggs',
      headerName: 'Total Eggs',
      flex: 1,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" fontWeight={600} color="primary">
            {params.row.summary.total_eggs?.toLocaleString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'avg_lay_percentage',
      headerName: 'Lay %',
      flex: 1.2,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => {
        const percentage = params.row.summary.avg_lay_percentage;
        const color = percentage >= 80 ? '#4caf50' : percentage >= 60 ? '#ff9800' : '#f44336';
        return (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            height="100%"
            width="100%"
          >
            <Typography variant="body2" fontWeight={600} sx={{ color, mb: 0.5 }}>
              {percentage.toFixed(1)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(percentage, 100)}
              sx={{
                height: 5,
                borderRadius: 1,
                backgroundColor: 'rgba(0,0,0,0.05)',
                '& .MuiLinearProgress-bar': { backgroundColor: color },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'day_over_day_diff',
      headerName: 'Day Change',
      flex: 0.9,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams) => {
        const diff = params.row.summary.day_over_day_diff;
        if (diff === null || diff === undefined) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            </Box>
          );
        }
        const color = diff > 0 ? '#4caf50' : diff < 0 ? '#f44336' : 'text.secondary';
        return (
          <Box display="flex" alignItems="center" gap={0.5} height="100%">
            {diff > 0 ? (
              <ArrowUpwardIcon fontSize="small" sx={{ color }} />
            ) : diff < 0 ? (
              <ArrowDownwardIcon fontSize="small" sx={{ color }} />
            ) : (
              <RemoveIcon fontSize="small" sx={{ color: '#ff9800' }} />
            )}
            <Typography variant="body2" fontWeight={600} sx={{ color }}>
              {diff > 0 ? `+${diff.toLocaleString()}` : diff.toLocaleString()}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'day_over_day_pct',
      headerName: 'Change %',
      flex: 0.9,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams) => {
        const pct = params.row.summary.day_over_day_pct;
        if (pct === null || pct === undefined) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            </Box>
          );
        }
        const color = pct > 0 ? '#4caf50' : pct < 0 ? '#f44336' : '#ff9800';
        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2" fontWeight={600} sx={{ color }}>
              {pct > 0 ? `+${pct}%` : `${pct}%`}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'trend',
      headerName: 'Trend',
      flex: 0.8,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams) => {
        const trend = params.row.overview.production_stats.performance_trend;
        return (
          <Box display="flex" alignItems="center" gap={0.5} height="100%">
            {getTrendIcon(trend)}
            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
              {trend}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'crates',
      headerName: 'Crates',
      flex: 0.7,
      minWidth: 85,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2">
            {params.row.summary.total_crates?.toLocaleString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={() => handleViewDetails(params.row.overview.bird_info.id)}
          sx={{ color: 'primary.main' }}
          title="View details"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const productions = productionsData?.data || [];
  const layingBirds = (birdsData?.data ?? []).filter((b: any) => b.is_laying);
  const selectedBird = layingBirds.find((b: any) => String(b.id) === formData.birds_id) ?? null;
  const isCagedBird = !!(selectedBird as any)?.cage_date;
  const cageBlocks = (selectedBirdBlocks as any[]).filter((b) => (b.type ?? 'rearing') === 'cage');

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(productionsFetching || statsFetching || birdsFetching || procurementsFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* Page Header */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #161616 0%, #1f1f1f 100%)',
          border: '1px solid rgba(255,152,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: '#66bb6a', width: 52, height: 52 }}
          >
            <EggIcon fontSize="medium" />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
            >
              Egg Production
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
              Monitor daily egg output, quality and laying rates per bird batch
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
            color: '#fff',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(255,152,0,0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
              boxShadow: '0 6px 16px rgba(255,152,0,0.4)',
            },
          }}
        >
          Record Production
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2.5} mb={3.5}>
          {[
            {
              rgb: '102,187,106',
              icon: <EggIcon />,
              label: "TODAY'S PRODUCTION",
              value: `${Math.floor(Number(stats.total_today || 0) / 30)} crates +${Number(stats.total_today || 0) % 30}`,
              sub: `${Number(stats.total_today || 0).toLocaleString()} eggs`,
            },
            {
              rgb: '255,152,0',
              icon: <TrendingUpIcon />,
              label: 'TOTAL (ALL TIME)',
              value: `${Math.floor(Number(stats.total_eggs || 0) / 30).toLocaleString()} crates`,
              sub: `${Number(stats.total_eggs || 0).toLocaleString()} eggs`,
            },
            {
              rgb: '129,199,132',
              icon: <CheckCircleIcon />,
              label: 'GOOD EGGS',
              value: `${Math.floor(Number(stats.total_good_eggs || 0) / 30).toLocaleString()} crates`,
              sub: `${
                typeof stats.good_eggs_percentage === 'number'
                  ? stats.good_eggs_percentage.toFixed(1)
                  : stats.good_eggs_percentage || '0'
              }% of total`,
            },
            {
              rgb: '244,67,54',
              icon: <CancelIcon />,
              label: 'BROKEN EGGS',
              value: stats.total_broken_eggs?.toLocaleString() ?? '0',
              sub: `${
                typeof stats.broken_eggs_percentage === 'number'
                  ? stats.broken_eggs_percentage.toFixed(1)
                  : stats.broken_eggs_percentage || '0'
              }% of total`,
            },
            {
              rgb: '156,39,176',
              icon: <TrendingUpIcon />,
              label: 'AVERAGE DAILY',
              value:
                typeof stats.average_daily === 'number'
                  ? stats.average_daily.toFixed(0)
                  : stats.average_daily || '0',
              sub: 'eggs per day',
            },
          ].map((card) => (
            <Grid key={card.label} size={{ xs: 12, sm: 6, md: 2.4 }}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filter Section */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
          border: '1px solid rgba(255, 152, 0, 0.3)',
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#ff9800' }}>
          Filter by Batch & Date Range
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
          <Autocomplete
            options={procurementsData?.data || []}
            getOptionLabel={(option: any) => option.identifier}
            value={
              procurementsData?.data?.find((p: any) => String(p.id) === filters.procurement_id) ??
              null
            }
            onChange={(_e, value: any) =>
              setFilters({ ...filters, procurement_id: value ? String(value.id) : '' })
            }
            sx={{ minWidth: 250 }}
            size="small"
            renderInput={(params) => <TextField {...params} label="Bird Batch" />}
          />
          <TextField
            label="From Date"
            name="date_from"
            type="date"
            value={filters.date_from}
            onChange={handleFilterChange}
            sx={{ minWidth: 180 }}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="To Date"
            name="date_to"
            type="date"
            value={filters.date_to}
            onChange={handleFilterChange}
            sx={{ minWidth: 180 }}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {(filters.procurement_id || filters.date_from || filters.date_to) && (
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              size="small"
              sx={{ height: 40 }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Filtered Results Summary */}
        {(filters.procurement_id || filters.date_from || filters.date_to) && (
          <Box mt={3} pt={3} borderTop="1px solid rgba(255, 152, 0, 0.2)">
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#ffa726' }}>
              Filtered Results Summary
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Paper
                sx={{
                  p: 2,
                  minWidth: 180,
                  flex: 1,
                  background: 'rgba(76, 175, 80, 0.1)',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(76, 175, 80, 0.8)' }}>
                  TOTAL PRODUCTION
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#66bb6a', mt: 1 }}>
                  {`${filteredTotals.totalCrates.toLocaleString()} crates + ${filteredTotals.totalPieces}`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {filteredTotals.totalEggs.toLocaleString()} total eggs
                </Typography>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  minWidth: 180,
                  flex: 1,
                  background: 'rgba(129, 199, 132, 0.1)',
                  border: '1px solid rgba(129, 199, 132, 0.3)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(129, 199, 132, 0.8)' }}>
                  GOOD EGGS
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#81c784', mt: 1 }}>
                  {`${filteredTotals.goodCrates.toLocaleString()} crates + ${filteredTotals.goodPieces}`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {filteredTotals.goodEggs.toLocaleString()} eggs
                </Typography>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  minWidth: 180,
                  flex: 1,
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(244, 67, 54, 0.8)' }}>
                  BROKEN EGGS
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#f44336', mt: 1 }}>
                  {filteredTotals.totalBroken.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {filteredTotals.totalEggs > 0
                    ? ((filteredTotals.totalBroken / filteredTotals.totalEggs) * 100).toFixed(1)
                    : 0}
                  % breakage rate
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Productions Table */}
      <Paper
        sx={{
          width: '100%',
          border: '1px solid rgba(255,152,0,0.2)',
          borderRadius: 2,
          overflow: 'hidden',
          p: 0,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
            borderBottom: '1px solid rgba(255,152,0,0.15)',
          }}
        >
          <EggIcon sx={{ color: '#66bb6a', fontSize: '1.1rem' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff' }}>
            Production Records
          </Typography>
          <Chip
            label={productions.length}
            size="small"
            sx={{
              bgcolor: 'rgba(102,187,106,0.12)',
              color: '#66bb6a',
              fontWeight: 700,
              height: 20,
            }}
          />
        </Box>
        <DataGrid
          rows={productions}
          columns={columns}
          getRowId={(row) => row.overview.bird_info.id}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          loading={isLoading}
          sx={{
            border: 0,
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: '0.8px',
              color: 'rgba(255,152,0,0.85)',
              textTransform: 'uppercase',
            },
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="record production"
        onClick={handleOpenDialog}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
          color: '#ffffff',
          boxShadow: '0px 6px 20px rgba(255, 152, 0, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
            boxShadow: '0px 8px 24px rgba(255, 152, 0, 0.5)',
          },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Create Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { border: '1px solid rgba(102,187,106,0.35)', borderRadius: 2 } },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#66bb6a' }}>
            Record Egg Production
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} mt={0.5}>
            Enter the daily egg production details
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
            <Autocomplete
              options={layingBirds}
              getOptionLabel={(b: any) => b.name}
              value={layingBirds.find((b: any) => String(b.id) === formData.birds_id) ?? null}
              onChange={(_e, val: any) =>
                setFormData({ ...formData, birds_id: val ? String(val.id) : '', block_id: '' })
              }
              fullWidth
              renderInput={(params) => <TextField {...params} label="Bird Batch" required />}
            />
            {isCagedBird && (
              <Autocomplete
                options={cageBlocks}
                getOptionLabel={(b: any) => b.name}
                value={cageBlocks.find((b: any) => String(b.id) === formData.block_id) ?? null}
                onChange={(_e, val: any) =>
                  setFormData({ ...formData, block_id: val ? String(val.id) : '' })
                }
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cage Block (Room)"
                    required
                    helperText="Select the cage room this production is from"
                  />
                )}
              />
            )}
            <TextField
              label="Production Date"
              name="production_date"
              type="date"
              value={formData.production_date}
              onChange={handleInputChange}
              fullWidth
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Number of Crates"
              name="crate_quantity"
              type="number"
              value={formData.crate_quantity}
              onChange={handleInputChange}
              fullWidth
              required
              helperText="1 crate = 30 eggs"
            />
            <TextField
              label="Surplus Eggs"
              name="surplus_quantity"
              type="number"
              value={formData.surplus_quantity}
              onChange={handleInputChange}
              fullWidth
              required
              helperText="Extra eggs not filling a complete crate"
            />
            <TextField
              label="Broken Eggs"
              name="broken_quantity"
              type="number"
              value={formData.broken_quantity}
              onChange={handleInputChange}
              fullWidth
              helperText="Number of broken eggs"
            />
            <TextField
              label="Remarks (Optional)"
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
              },
            }}
          >
            {createMutation.isPending ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EggProductionPage;
